/**
 * Standort: die Außenansicht mit Gebäuden und einer steuerbaren Figur.
 *
 * Wohnhaus, Rechenzentrum, Büro und Netzwerkzentrale stehen nebeneinander, in
 * zurückhaltenden Farben. Die Figur läuft mit ← → bzw. A D oder per Klick auf
 * den Boden. Steht sie vor einer Tür, geht man per Klick auf die Tür oder mit E
 * hinein. Die Tür aus der Ferne anzuklicken läuft die Figur dorthin.
 *
 * Die Position ist nur eine Bequemlichkeit im Browser (localStorage).
 */
import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader, Stat, StatGrid } from '../components/ui';
import { useGame, useMetrics } from '../store/game';
import { money, num } from '../lib/format';
import { loadAppearance, saveAppearance, type Appearance } from '../lib/appearance';
import { PlayerFigure } from '../components/figur/PlayerFigure';
import { AppearanceEditor } from '../components/figur/AppearanceEditor';
import { Button, Modal } from '../components/ui';

const SCENE_W = 1200;
const SCENE_H = 420;
const SPEED = 170;
const GROUND_Y = 346;
/** Tiefe, in der sich die Figur bewegen darf: Gehweg bis in die Fahrbahn. */
const MIN_Y = 344;
const MAX_Y = 384;
function clampY(value: number) {
  return Math.min(MAX_Y, Math.max(MIN_Y, value));
}
const DOOR_REACH = 26;
const START_X = 330;
const PLAYER_KEY = 'hostingsim.standort.x';

type Zone = 'haus' | 'rechenzentrum' | 'buero' | 'netz';

const DOOR_X: Record<Zone, number> = { haus: 240, rechenzentrum: 605, buero: 900, netz: 1110 };
const ZONE_LABEL: Record<Zone, string> = {
  haus: 'ins Wohnhaus gehen',
  rechenzentrum: 'ins Rechenzentrum gehen',
  buero: 'ins Büro gehen',
  netz: 'in die Netzwerkzentrale gehen',
};

function zoneAt(x: number): Zone | null {
  const hit = (Object.keys(DOOR_X) as Zone[]).find((z) => Math.abs(x - DOOR_X[z]) <= DOOR_REACH);
  return hit ?? null;
}

function clampX(x: number) {
  return Math.min(SCENE_W - 30, Math.max(30, x));
}

function readStart(): number {
  try {
    const raw = window.localStorage.getItem(PLAYER_KEY);
    const value = raw === null ? NaN : Number(raw);
    return Number.isFinite(value) ? clampX(value) : START_X;
  } catch {
    return START_X;
  }
}

function saveStart(x: number) {
  try {
    window.localStorage.setItem(PLAYER_KEY, String(Math.round(x)));
  } catch {
    /* Ohne Speicher startet die Figur eben von vorn. */
  }
}

export function Zuhause() {
  const world = useGame((s) => s.world);
  const gameId = useGame((s) => s.gameId);
  const metrics = useMetrics();
  const navigate = useNavigate();

  const [x, setX] = useState(readStart);
  const [facing, setFacing] = useState<1 | -1>(1);
  const [appearance, setAppearance] = useState<Appearance>(() => loadAppearance(gameId));
  const [designing, setDesigning] = useState(false);
  const [walking, setWalking] = useState(false);
  const [phase, setPhase] = useState(0);

  const xRef = useRef(x);
  const [y, setY] = useState(GROUND_Y);
  const yRef = useRef(GROUND_Y);
  const keys = useRef(new Set<string>());
  const target = useRef<{ x: number; y?: number; enter: Zone | null } | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const gameIdRef = useRef(gameId);
  gameIdRef.current = gameId;

  const enter = (zone: Zone) => {
    if (gameIdRef.current) navigate(`/game/${gameIdRef.current}/innen/${zone}`);
  };
  const enterRef = useRef(enter);
  enterRef.current = enter;

  // Bewegung mit fester Geschwindigkeit: Tasten oder Ziel per Klick.
  useEffect(() => {
    let last = performance.now();
    let ph = 0;
    let lastSave = 0;

    const frame = (now: number) => {
      const dt = Math.min(0.25, (now - last) / 1000);
      last = now;
      let dx = 0;
      let dy = 0;
      if (keys.current.has('ArrowLeft') || keys.current.has('a') || keys.current.has('A')) dx -= 1;
      if (keys.current.has('ArrowRight') || keys.current.has('d') || keys.current.has('D')) dx += 1;
      if (keys.current.has('ArrowUp') || keys.current.has('w') || keys.current.has('W')) dy -= 1;
      if (keys.current.has('ArrowDown') || keys.current.has('s') || keys.current.has('S')) dy += 1;
      if (dx === 0 && dy === 0 && target.current) {
        const tx = target.current.x - xRef.current;
        const ty = (target.current.y ?? yRef.current) - yRef.current;
        const dist = Math.hypot(tx, ty);
        if (dist <= 2) {
          const arrived = target.current.enter;
          target.current = null;
          if (arrived) enterRef.current(arrived);
        } else {
          dx = tx / dist;
          dy = ty / dist;
        }
      }

      const moving = dx !== 0 || dy !== 0;
      if (moving) {
        const len = Math.hypot(dx, dy) || 1;
        xRef.current = clampX(xRef.current + (dx / len) * SPEED * dt);
        yRef.current = clampY(yRef.current + (dy / len) * SPEED * dt);
        ph += dt * 10;
        if (Math.abs(dx) > 0.01) setFacing(dx > 0 ? 1 : -1);
        setX(xRef.current);
        setY(yRef.current);
      }
      setWalking(moving);
      setPhase(moving ? ph : 0);
      if (!moving && now - lastSave > 500) {
        saveStart(xRef.current);
        lastSave = now;
      }
    };
    const timer = window.setInterval(() => frame(performance.now()), 16);
    return () => window.clearInterval(timer);
  }, []);

  // Tasten: Bewegung halten und E/Enter an der Tür.
  useEffect(() => {
    const isTyping = (el: EventTarget | null) => {
      const node = el as HTMLElement | null;
      return node?.tagName === 'INPUT' || node?.tagName === 'TEXTAREA' || node?.tagName === 'SELECT' || node?.isContentEditable === true;
    };
    const down = (e: globalThis.KeyboardEvent) => {
      if (isTyping(e.target)) return;
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', ' '].includes(e.key)) e.preventDefault();
      keys.current.add(e.key);
      if (e.key === 'e' || e.key === 'E' || e.key === 'Enter') {
        const zone = zoneAt(xRef.current);
        if (zone) enterRef.current(zone);
      }
    };
    const up = (e: globalThis.KeyboardEvent) => keys.current.delete(e.key);
    const blur = () => keys.current.clear();
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    window.addEventListener('blur', blur);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
      window.removeEventListener('blur', blur);
    };
  }, []);

  if (!world || !metrics || !gameId) return null;
  const storage = Object.keys(world.deliveries).length;
  const installing = Object.values(world.deliveries).filter((d) => d.install).length;
  const racks = Object.keys(world.racks).length;
  const city = Object.values(world.datacenters)[0]?.city ?? 'Standort';
  const zone = zoneAt(x);

  /** Klick auf den Boden: die Figur läuft dorthin. */
  const onScene = (e: MouseEvent<SVGSVGElement>) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const sceneX = ((e.clientX - rect.left) / rect.width) * SCENE_W;
    const sceneY = ((e.clientY - rect.top) / rect.height) * SCENE_H;
    target.current = { x: clampX(sceneX), y: clampY(sceneY), enter: null };
  };

  /** Klick auf eine Tür: davor sofort hinein, sonst erst hinlaufen. */
  const onDoor = (z: Zone) => (e: MouseEvent) => {
    e.stopPropagation();
    if (Math.abs(xRef.current - DOOR_X[z]) <= DOOR_REACH) enter(z);
    else target.current = { x: DOOR_X[z], enter: z };
  };
  const onDoorKey = (z: Zone) => (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (Math.abs(xRef.current - DOOR_X[z]) <= DOOR_REACH) enter(z);
      else target.current = { x: DOOR_X[z], enter: z };
    }
  };

  const legSwing = walking ? Math.sin(phase) * 6 : 0;

  return (
    <>
      <PageHeader
        title="Standort"
        description={`Gelände ${city}. Mit ← → ↑ ↓ oder WASD auf Gehweg und Straße laufen, an einer Tür per Klick oder E hineingehen.`}
        actions={
          <Button variant="secondary" onClick={() => setDesigning(true)}>
            Figur gestalten
          </Button>
        }
      />

      <StatGrid cols={3}>
        <Stat label="Liquidität" value={money(metrics.company.cash)} />
        <Stat label="Im Lager" value={num(storage)} hint={installing > 0 ? `${installing} im Einbau` : 'nichts im Einbau'} />
        <Stat label="Racks" value={num(racks)} hint={city} />
      </StatGrid>

      <div className="relative mt-4 overflow-hidden rounded-md border border-slate-700">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SCENE_W} ${SCENE_H}`}
          className="block h-auto w-full select-none"
          role="img"
          aria-label="Gelände mit Wohnhaus, Rechenzentrum, Büro und Netzwerkzentrale"
          onClick={onScene}
        >
          <Defs />
          <Sky />
          <Ground />

          <DecorBack />
          <House />
          <g role="button" tabIndex={0} aria-label="Haustür: Heim-PC öffnen" onClick={onDoor('haus')} onKeyDown={onDoorKey('haus')} className="cursor-pointer focus:outline-none">
            <rect x={DOOR_X.haus - 24} y="262" width="48" height="82" fill="transparent" />
            <text x={DOOR_X.haus} y="398" textAnchor="middle" className="fill-slate-100" fontSize="20" fontWeight="600">Wohnhaus</text>
            <text x={DOOR_X.haus} y="414" textAnchor="middle" className="fill-slate-400" fontSize="12">Tür · Heim-PC</text>
          </g>

          <DataCenter city={city} racks={racks} />
          <g role="button" tabIndex={0} aria-label="Eingang Rechenzentrum: zum Einbau" onClick={onDoor('rechenzentrum')} onKeyDown={onDoorKey('rechenzentrum')} className="cursor-pointer focus:outline-none">
            <rect x={DOOR_X.rechenzentrum - 36} y="284" width="72" height="62" fill="transparent" />
          </g>

          <Office />
          <g role="button" tabIndex={0} aria-label="Bürotür: Kunden und Finanzen" onClick={onDoor('buero')} onKeyDown={onDoorKey('buero')} className="cursor-pointer focus:outline-none">
            <rect x={DOOR_X.buero - 30} y="282" width="60" height="62" fill="transparent" />
            <text x={DOOR_X.buero} y="398" textAnchor="middle" className="fill-slate-100" fontSize="20" fontWeight="600">Büro</text>
            <text x={DOOR_X.buero} y="414" textAnchor="middle" className="fill-slate-400" fontSize="12">Kunden · Finanzen</text>
          </g>

          <NetBuilding />
          <g role="button" tabIndex={0} aria-label="Tür der Netzwerkzentrale: Router und BGP" onClick={onDoor('netz')} onKeyDown={onDoorKey('netz')} className="cursor-pointer focus:outline-none">
            <rect x={DOOR_X.netz - 30} y="286" width="60" height="58" fill="transparent" />
            <text x={DOOR_X.netz} y="398" textAnchor="middle" className="fill-slate-100" fontSize="20" fontWeight="600">Netzwerk</text>
            <text x={DOOR_X.netz} y="414" textAnchor="middle" className="fill-slate-400" fontSize="12">Router · BGP</text>
          </g>

          <Facade />
          <Traffic />
          <Player x={x} y={y} facing={facing} legSwing={legSwing} a={appearance} />
        </svg>

        {zone && (
          <div className="pointer-events-none absolute bottom-3 left-1/2 -translate-x-1/2 rounded bg-slate-900/90 px-3 py-1.5 text-sm text-slate-100 shadow">
            <kbd className="rounded bg-slate-700 px-1.5 py-0.5 font-mono text-xs">E</kbd> oder Klick auf die Tür: {ZONE_LABEL[zone]}
          </div>
        )}
      </div>
      <Modal open={designing} onClose={() => setDesigning(false)} title="Deine Figur" wide>
        <AppearanceEditor
          value={appearance}
          onChange={(next) => {
            setAppearance(next);
            saveAppearance(gameId, next);
          }}
        />
      </Modal>
      <p className="mt-2 text-xs text-slate-500">← → ↑ ↓ oder WASD laufen · Klick auf den Boden läuft dorthin · Klick auf eine Tür geht hinein, wenn die Figur davor steht</p>

    </>
  );
}

// ---------------------------------------------------------------------------
// Gelände
// ---------------------------------------------------------------------------

const C = {
  wall: '#d9d4c7',
  roof: '#4b5563',
  brick: '#8a5a44',
  steel: '#64748b',
  steelDark: '#334155',
  glass: '#1e293b',
  lamp: '#fcd34d',
  grass: '#4b6b4a',
  path: '#a8a29e',
};

function Defs() {
  return (
    <defs>
      <linearGradient id="sky" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#1f2a37" />
        <stop offset="1" stopColor="#64748b" />
      </linearGradient>
      <pattern id="chain" width="8" height="8" patternUnits="userSpaceOnUse">
        <path d="M0 0 L8 8 M8 0 L0 8" stroke="#94a3b8" strokeWidth="0.8" />
      </pattern>
    </defs>
  );
}

function Sky() {
  return (
    <>
      <rect width={SCENE_W} height={SCENE_H} fill="url(#sky)" />
      <circle cx="1120" cy="60" r="22" fill="#e2e8f0" opacity="0.5" />
    </>
  );
}

function Ground() {
  return (
    <>
      <rect y="340" width={SCENE_W} height="80" fill={C.grass} />
      <rect y="338" width={SCENE_W} height="4" fill="#3b5a3b" />
      {/* Gehweg mit Bordstein */}
      <rect y="340" width={SCENE_W} height="8" fill="#a8a29e" />
      <rect y="348" width={SCENE_W} height="2" fill="#e7e5e4" />
      {/* Fahrbahn */}
      <rect y="350" width={SCENE_W} height="38" fill="#3f4650" />
      <rect y="351" width={SCENE_W} height="1" fill="#e2e8f0" opacity="0.5" />
      <rect y="387" width={SCENE_W} height="1" fill="#e2e8f0" opacity="0.5" />
      {/* Gestrichelte Mittellinie */}
      {Array.from({ length: 40 }).map((_, i) => (
        <rect key={i} x={i * 32} y="368" width="18" height="2.5" fill="#e2e8f0" opacity="0.8" />
      ))}
    </>
  );
}

function Lamp({ x, y }: { x: number; y: number }) {
  return (
    <g>
      <line x1={x} y1={y} x2={x} y2={y - 60} stroke={C.steelDark} strokeWidth="3" />
      <line x1={x} y1={y - 60} x2={x + 8} y2={y - 60} stroke={C.steelDark} strokeWidth="3" />
      <circle cx={x + 8} cy={y - 57} r="3" fill={C.lamp} />
    </g>
  );
}

// ---------------------------------------------------------------------------
// Wohnhaus
// ---------------------------------------------------------------------------

function House() {
  return (
    <g>
      {/* Gartenzaun */}
      <rect x="18" y="304" width="118" height="4" fill="#e7e5e4" />
      <rect x="18" y="322" width="118" height="4" fill="#e7e5e4" />
      {Array.from({ length: 9 }).map((_, i) => (
        <rect key={i} x={20 + i * 14} y="292" width="4" height="50" fill="#e7e5e4" />
      ))}
      {/* Baum */}
      <rect x="62" y="264" width="6" height="78" fill="#57534e" />
      <circle cx="65" cy="236" r="28" fill="#3f5f3f" />
      {/* Schornstein */}
      <rect x="294" y="146" width="20" height="58" fill={C.brick} />
      {/* Haupthaus */}
      <rect x="150" y="200" width="180" height="140" fill={C.wall} stroke="#78716c" strokeWidth="2" />
      <polygon points="134,206 240,120 346,206" fill={C.roof} stroke="#374151" strokeWidth="2" />
      {/* Solaranlage: Module liegen flach auf der rechten Dachfläche, parallel zur Traufe */}
      <SolarArray />
      {/* Fenster links mit Licht */}
      <rect x="172" y="226" width="44" height="44" fill={C.glass} stroke="#78716c" strokeWidth="2" />
      <rect x="176" y="230" width="36" height="36" fill={C.lamp} opacity="0.5" />
      <line x1="194" y1="226" x2="194" y2="270" stroke="#78716c" strokeWidth="1.5" />
      {/* Fenster rechts */}
      <rect x="264" y="226" width="44" height="44" fill={C.glass} stroke="#78716c" strokeWidth="2" />
      <line x1="286" y1="226" x2="286" y2="270" stroke="#78716c" strokeWidth="1.5" />
      {/* Eingangstür */}
      <rect x="222" y="272" width="36" height="68" fill="#57534e" stroke="#292524" strokeWidth="2" className="door" />
      <circle cx="252" cy="306" r="2" fill="#e7e5e4" />
      {/* Auto in der Einfahrt */}
      <g transform="translate(372 336)">
        <rect x="0" y="0" width="54" height="16" rx="4" fill="#6b7280" stroke="#374151" strokeWidth="1.5" />
        <path d="M 10 0 L 16 -10 L 38 -10 L 44 0 Z" fill="#4b5563" stroke="#374151" strokeWidth="1.5" />
        <circle cx="12" cy="16" r="5" fill="#111827" />
        <circle cx="42" cy="16" r="5" fill="#111827" />
      </g>
    </g>
  );
}

// ---------------------------------------------------------------------------
// Rechenzentrum
// ---------------------------------------------------------------------------

function Fan({ cx, cy, r = 12 }: { cx: number; cy: number; r?: number }) {
  return (
    <g>
      <circle cx={cx} cy={cy} r={r} fill={C.steelDark} stroke={C.steel} strokeWidth="2" />
      <g transform={`translate(${cx} ${cy})`} fill={C.steel}>
        <ellipse rx={r - 3} ry="2.5" />
        <ellipse rx={r - 3} ry="2.5" transform="rotate(60)" />
        <ellipse rx={r - 3} ry="2.5" transform="rotate(120)" />
      </g>
    </g>
  );
}

function Camera({ x, y }: { x: number; y: number }) {
  return (
    <g>
      <line x1={x} y1={y} x2={x} y2={y - 46} stroke={C.steelDark} strokeWidth="3" />
      <rect x={x - 6} y={y - 52} width="14" height="8" rx="2" fill="#0f172a" />
    </g>
  );
}

function DataCenter({ city, racks }: { city: string; racks: number }) {
  return (
    <g>
      {/* Zaun, Kameras, Beleuchtung */}
      <rect x="430" y="322" width="360" height="22" fill="url(#chain)" stroke="#94a3b8" strokeWidth="1" />
      <Camera x={446} y={340} />
      <Camera x={776} y={340} />
      <Lamp x={520} y={340} />
      <Lamp x={700} y={340} />
      {/* Notstrom-Container und Tank */}
      <rect x="448" y="290" width="58" height="50" fill={C.steel} stroke={C.steelDark} strokeWidth="2" />
      <rect x="478" y="304" width="22" height="30" rx="2" fill={C.steelDark} />
      <rect x="510" y="308" width="6" height="32" fill={C.steel} />
      <rect x="520" y="308" width="6" height="32" fill={C.steel} />
      {/* Gebäude */}
      <rect x="470" y="142" width="270" height="200" fill={C.steelDark} stroke="#1e293b" strokeWidth="3" />
      <rect x="470" y="142" width="270" height="12" fill={C.steel} />
      {/* Dachkühlung */}
      {[488, 558, 628].map((x) => (
        <g key={x}>
          <rect x={x} y="112" width="58" height="30" rx="2" fill="#cbd5e1" stroke={C.steel} strokeWidth="2" />
          <Fan cx={x + 29} cy={127} r={11} />
        </g>
      ))}
      {/* Antenne */}
      <line x1="740" y1="142" x2="740" y2="74" stroke={C.steel} strokeWidth="2" />
      <circle cx="740" cy="72" r="2" fill="#ef4444" />
      {/* Serverfenster */}
      {[0, 1, 2].map((row) =>
        Array.from({ length: 8 }).map((_, col) => {
          const fx = 492 + col * 30;
          const fy = 214 + row * 36;
          const lit = (row + col) % 3 !== 0;
          return (
            <g key={`${row}-${col}`}>
              <rect x={fx} y={fy} width="20" height="22" fill="#0f172a" stroke={C.steel} strokeWidth="0.8" />
              <rect x={fx + 3} y={fy + 5} width="14" height="2" fill={lit ? '#7dd3fc' : '#1e293b'} />
              <rect x={fx + 3} y={fy + 12} width="14" height="2" fill={lit ? '#86efac' : '#1e293b'} />
            </g>
          );
        }),
      )}
      {/* Schild */}
      <rect x="500" y="170" width="210" height="24" rx="2" fill="#0f172a" />
      <text x="605" y="187" textAnchor="middle" fill="#cbd5e1" fontSize="12" fontWeight="700" letterSpacing="2">RECHENZENTRUM</text>
      {/* Ladebucht */}
      <rect x="484" y="262" width="56" height="56" fill="#0f172a" stroke={C.steel} strokeWidth="1.5" />
      {/* Eingang */}
      <rect x="572" y="284" width="66" height="52" fill="#0f172a" stroke={C.steel} strokeWidth="2" className="door" />
      <rect x="576" y="288" width="58" height="44" fill="#334155" opacity="0.4" />
      <text x="605" y="398" textAnchor="middle" className="fill-slate-100" fontSize="20" fontWeight="600">Rechenzentrum</text>
      <text x="605" y="414" textAnchor="middle" className="fill-slate-400" fontSize="12">{city} · {racks} Rack(s) · Tür</text>
    </g>
  );
}

// ---------------------------------------------------------------------------
// Büro und Netzwerkzentrale
// ---------------------------------------------------------------------------

function Office() {
  return (
    <g>
      <rect x="820" y="186" width="170" height="156" fill="#e7e5e4" stroke="#78716c" strokeWidth="2" />
      <rect x="820" y="176" width="170" height="12" fill={C.steel} />
      {[0, 1, 2, 3].map((row) =>
        [0, 1, 2].map((col) =>
          row >= 2 && col === 1 ? null : (
          <rect key={`${row}-${col}`} x={836 + col * 50} y={214 + row * 30} width="32" height="20" fill={(row + col) % 2 ? '#cbd5e1' : '#f5f5f4'} stroke="#78716c" strokeWidth="1" />
          )
        ),
      )}
      <polygon points="880,282 905,272 930,282" fill={C.steelDark} />
      <rect x="884" y="282" width="32" height="60" fill={C.steelDark} stroke="#1e293b" strokeWidth="2" className="door" />
      <rect x="860" y="156" width="90" height="20" rx="2" fill="#e7e5e4" stroke="#78716c" strokeWidth="1" />
      <text x="905" y="170" textAnchor="middle" fill="#334155" fontSize="11" fontWeight="700" letterSpacing="2">BÜRO</text>
    </g>
  );
}

function NetBuilding() {
  return (
    <g>
      <line x1="1070" y1="150" x2="1070" y2="82" stroke={C.steel} strokeWidth="2" />
      <line x1="1060" y1="104" x2="1080" y2="104" stroke={C.steel} strokeWidth="2" />
      <rect x="1040" y="150" width="140" height="192" fill={C.steelDark} stroke="#0f172a" strokeWidth="2" />
      <rect x="1040" y="142" width="140" height="10" fill={C.steel} />
      <rect x="1062" y="168" width="96" height="22" fill="#0f172a" />
      <text x="1110" y="184" textAnchor="middle" fill="#cbd5e1" fontSize="11" fontWeight="700" letterSpacing="1.5">NETZWERK</text>
      {[0, 1, 2, 3].map((row) =>
        Array.from({ length: 6 }).map((_, col) => (
          <rect key={`${row}-${col}`} x={1052 + col * 19} y={204 + row * 22} width="13" height="12" fill="#0f172a" stroke={C.steel} strokeWidth="0.8" />
        )),
      )}
      <rect x="1036" y="206" width="4" height="136" fill={C.steel} />
      <rect x="1094" y="290" width="32" height="52" fill="#0f172a" stroke={C.steel} strokeWidth="2" className="door" />
      <rect x="1150" y="300" width="28" height="42" fill="#cbd5e1" stroke={C.steel} strokeWidth="2" />
      <Fan cx={1164} cy={316} r={8} />
      <Lamp x={1020} y={340} />
    </g>
  );
}

// ---------------------------------------------------------------------------
// Spielfigur
// ---------------------------------------------------------------------------

function Player({ x, y, facing, legSwing, a }: { x: number; y: number; facing: 1 | -1; legSwing: number; a: Appearance }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${facing} 1)`} aria-label="Deine Figur">
      <PlayerFigure a={a} legSwing={legSwing} />
    </g>
  );
}

// ---------------------------------------------------------------------------
// Ausstattung des Geländes: Bäume, Beete, parkende Autos, Fassadenstruktur
// ---------------------------------------------------------------------------

function Tree({ x, height = 90, crown = 30 }: { x: number; height?: number; crown?: number }) {
  return (
    <g>
      <rect x={x - 3} y={340 - height * 0.45} width="6" height={height * 0.45} fill="#57534e" />
      <circle cx={x} cy={340 - height * 0.45 - crown * 0.6} r={crown} fill="#3f5f3f" />
      <circle cx={x - crown * 0.45} cy={340 - height * 0.45 - crown * 0.1} r={crown * 0.7} fill="#4b7050" />
      <circle cx={x + crown * 0.5} cy={340 - height * 0.45 - crown * 0.2} r={crown * 0.65} fill="#355a38" />
    </g>
  );
}

function ParkedCar({ x, y, color }: { x: number; y: number; color: string }) {
  return (
    <g transform={`translate(${x} ${y})`}>
      <rect x="0" y="0" width="46" height="13" rx="3" fill={color} stroke="#1f2937" strokeWidth="1" />
      <path d="M 9 0 L 14 -8 L 32 -8 L 37 0 Z" fill={color} stroke="#1f2937" strokeWidth="1" />
      <path d="M 16 -1 L 19 -6 L 29 -6 L 32 -1 Z" fill="#cbd5e1" opacity="0.8" />
      <circle cx="11" cy="13" r="4" fill="#111827" />
      <circle cx="36" cy="13" r="4" fill="#111827" />
    </g>
  );
}

function DecorBack() {
  return (
    <g pointerEvents="none">


      <Tree x={414} height={110} crown={34} />
      <Tree x={790} height={100} crown={30} />
      <Tree x={1195} height={120} crown={36} />

      <rect x="805" y="334" width="180" height="8" rx="2" fill="#44403c" />
      {Array.from({ length: 16 }).map((_, i) => (
        <circle key={i} cx={812 + i * 11} cy={332} r="2.2" fill={i % 3 === 0 ? '#e7e5e4' : '#a8a29e'} />
      ))}



      <Lamp x={330} y={340} />
      <Lamp x={780} y={340} />
      <Lamp x={1010} y={340} />
    </g>
  );
}

/** Fassadenstruktur und Fensterlichter: liegen über den Wänden der Gebäude. */
/** Fahrende Autos auf der Straße: liegen vor den Gebäuden, aber unter den Türen. */
function Traffic() {
  return (
    <g pointerEvents="none">
      <g>
        <animateTransform attributeName="transform" type="translate" from="-80 0" to="1300 0" dur="16s" repeatCount="indefinite" />
        <ParkedCar x={0} y={360} color="#7f1d1d" />
      </g>
      <g>
        <animateTransform attributeName="transform" type="translate" from="1300 0" to="-80 0" dur="22s" repeatCount="indefinite" />
        <ParkedCar x={0} y={378} color="#334155" />
      </g>
    </g>
  );
}

function Facade() {
  return (
    <g pointerEvents="none">
      <defs>
        <pattern id="brickDecor" width="14" height="7" patternUnits="userSpaceOnUse">
          <path d="M0 7 H14 M0 0 H14 M7 0 V7 M0 3.5 H14" stroke="#7c5a4a" strokeWidth="0.6" opacity="0.35" />
        </pattern>
        <pattern id="plasterDecor" width="24" height="24" patternUnits="userSpaceOnUse">
          <path d="M0 24 H24 M24 0 V24" stroke="#94a3b8" strokeWidth="0.4" opacity="0.4" />
        </pattern>
      </defs>
      <rect x="150" y="200" width="180" height="140" fill="url(#brickDecor)" />
      <rect x="820" y="186" width="170" height="156" fill="url(#plasterDecor)" />
      <rect x="1040" y="150" width="140" height="192" fill="url(#plasterDecor)" opacity="0.6" />
      {[0, 1, 2, 3].map((row) =>
        [0, 1, 2].map((col) =>
          row >= 2 && col === 1 ? null : (
          <rect key={`r${row}-${col}`} x={840 + col * 50} y={217 + row * 30} width="5" height="14" fill="#ffffff" opacity="0.35" />
        )),
      )}

      {[[1,0],[0,2],[3,0],[1,2]].map(([row, col], i) => (
        <rect key={`lit${i}`} x={836 + col * 50} y={214 + row * 30} width="32" height="20" fill="#fde68a" opacity="0.55" />
      ))}
      {[[0,1],[2,0],[3,2]].map(([row, col], i) => (
        <rect key={`litn${i}`} x={1054 + col * 19} y={204 + row * 22} width="13" height="12" fill="#fde68a" opacity="0.5" />
      ))}

    </g>
  );
}

/**
 * Solarmodule auf der rechten Dachfläche. Die Reihen folgen der Dachschräge
 * von First (240,120) bis Traufe (346,206), leicht nach innen versetzt.
 */
function SolarArray() {
  const ridge = { x: 240, y: 120 };
  const eave = { x: 346, y: 206 };
  const inward = { x: -4, y: 6.8 };
  const cells: JSX.Element[] = [];
  const rows = 2;
  const cols = 4;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const t0 = 0.3 + (c / cols) * 0.55;
      const t1 = 0.3 + ((c + 1) / cols) * 0.55 - 0.02;
      const o0 = 4 + r * 9;
      const o1 = o0 + 7;
      const at = (t: number, o: number) => {
        const x = ridge.x + (eave.x - ridge.x) * t + inward.x * (o / 8);
        const y = ridge.y + (eave.y - ridge.y) * t + inward.y * (o / 8);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      };
      cells.push(
        <polygon key={`${r}-${c}`} points={`${at(t0, o0)} ${at(t1, o0)} ${at(t1, o1)} ${at(t0, o1)}`} fill="#1e3a5f" stroke="#94a3b8" strokeWidth="0.6" />,
      );
    }
  }
  return <g>{cells}</g>;
}
